/* Дараалал самбар — server-тэй (React Query, 7с тутамд refetch) */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cwApi } from '@/api/carwash';
import { CancelDialog } from '@/components/bits';
import { tug } from '@/shared/format';
import { auth } from '@/shared/auth/storage';
import type { IntakeEntry, QueueCar } from '@/shared/types';
import { useToast } from '@/app/providers/ToastProvider';
import { useShellUi } from '@/features/shell/ShellLayout';
import { MobileBoard, QueueContent } from './board';
import { IntakeDrawer } from './IntakeDrawer';
import { PaymentModal } from './PaymentModal';

export function QueuePage() {
  const { isMobile, stats, intakeOpen, setIntakeOpen, openIntake } = useShellUi();
  const toast = useToast();
  const qc = useQueryClient();

  const [now, setNow] = useState(Date.now());
  const [query, setQuery] = useState('');
  const [cancelTarget, setCancelTarget] = useState<QueueCar | null>(null);
  const [payTarget, setPayTarget] = useState<QueueCar | null>(null);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const queueQ = useQuery({
    queryKey: ['cw', 'queue'],
    queryFn: cwApi.queue,
    refetchInterval: 7_000,
  });
  const cars = queueQ.data ?? [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['cw', 'queue'] });
    qc.invalidateQueries({ queryKey: ['cw', 'today'] });
    qc.invalidateQueries({ queryKey: ['cw', 'washers'] });
  };
  const onError = (e: unknown) => toast(e instanceof Error ? e.message : 'Алдаа гарлаа.');

  const advanceMut = useMutation({
    mutationFn: (id: string) => cwApi.advance(id),
    onSuccess: invalidate,
    onError,
  });

  const cancelMut = useMutation({
    mutationFn: (id: string) => cwApi.cancel(id),
    onSuccess: (_d, _id) => {
      invalidate();
      setCancelTarget(null);
    },
    onError,
  });

  const createMut = useMutation({
    mutationFn: (entry: IntakeEntry) => cwApi.createOrder(entry),
    onSuccess: (dto) => {
      invalidate();
      setIntakeOpen(false);
      toast(dto.Status === 1 ? `Угааж эхэллээ · ${dto.Plate}` : `Дараалалд нэмэгдлээ · ${dto.Plate}`);
    },
    onError,
  });

  const onAdvance = (car: QueueCar) => {
    if (car.status === 'done') setPayTarget(car);
    else advanceMut.mutate(car.id);
  };

  const confirmCancel = (car: QueueCar) => {
    cancelMut.mutate(car.id, {
      onSuccess: () => toast(`Цуцлагдлаа · ${car.plate}`),
    });
  };

  const finalizePayment = (_car: QueueCar, paidTotal: number) => {
    invalidate();
    qc.invalidateQueries({ queryKey: ['cw', 'customers'] });
    setPayTarget(null);
    toast(`Төлбөр амжилттай · ${tug(paidTotal)}`);
  };

  /* Хүлээж буй машины дарааллын байр (орж ирсэн цагаар) */
  const posMap: Record<string, number> = {};
  cars.filter((c) => c.status === 'waiting')
    .sort((a, b) => a.enteredAt - b.enteredAt)
    .forEach((c, i) => { posMap[c.id] = i; });

  const currentWasher = auth.displayName ?? '';
  const boardProps = {
    cars, now, stats, onAdvance,
    onCancel: setCancelTarget,
    posMap, query, setQuery,
    onIntake: openIntake,
    currentWasher,
  };

  return (
    <>
      {isMobile
        ? <MobileBoard {...boardProps} />
        : <QueueContent cars={cars} now={now} onAdvance={onAdvance} onCancel={setCancelTarget}
            posMap={posMap} query={query} setQuery={setQuery} />}

      <IntakeDrawer open={intakeOpen} mobile={isMobile} onClose={() => setIntakeOpen(false)}
        onSubmit={(entry) => createMut.mutate(entry)} busy={createMut.isPending} />
      <PaymentModal open={!!payTarget} car={payTarget} mobile={isMobile}
        onClose={() => setPayTarget(null)} onFinalize={finalizePayment} />
      <CancelDialog car={cancelTarget} onClose={() => setCancelTarget(null)} onConfirm={confirmCancel} />
    </>
  );
}
